/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useEuiTheme } from '@elastic/eui'

import emptySolutionImg from '@modules/discovery-questions-pages/files/emptySolution.svg'
import elasticsearchSolutionImg from '@modules/discovery-questions-pages/files/elasticsearchSolution.svg'
import observabilitySolutionImg from '@modules/discovery-questions-pages/files/observabilitySolution.svg'
import securitySolutionImg from '@modules/discovery-questions-pages/files/securitySolution.svg'
import emptySolutionDarkImg from '@modules/discovery-questions-pages/files/emptySolution_dark.svg'
import elasticsearchSolutionDarkImg from '@modules/discovery-questions-pages/files/elasticsearchSolution_dark.svg'
import observabilitySolutionDarkImg from '@modules/discovery-questions-pages/files/observabilitySolution_dark.svg'
import securitySolutionDarkImg from '@modules/discovery-questions-pages/files/securitySolution_dark.svg'
import emptySolutionLargeImg from '@modules/discovery-questions-pages/files/emptySolution_large.svg'
import elasticsearchSolutionLargeImg from '@modules/discovery-questions-pages/files/elasticsearchSolution_large.svg'
import observabilitySolutionLargeImg from '@modules/discovery-questions-pages/files/observabilitySolution_large.svg'
import securitySolutionLargeImg from '@modules/discovery-questions-pages/files/securitySolution_large.svg'
import emptySolutionLargeDarkImg from '@modules/discovery-questions-pages/files/emptySolution_large_dark.svg'
import elasticsearchSolutionLargeDarkImg from '@modules/discovery-questions-pages/files/elasticsearchSolution_large_dark.svg'
import observabilitySolutionLargeDarkImg from '@modules/discovery-questions-pages/files/observabilitySolution_large_dark.svg'
import securitySolutionLargeDarkImg from '@modules/discovery-questions-pages/files/securitySolution_large_dark.svg'

import type { SolutionType } from './types'
/**
 * Utility function to get solution images and colors without state management
 */
export const useSolutionMapping = () => {
  const { colorMode } = useEuiTheme()

  const getThemeImage = (lightImg: string, darkImg: string) =>
    colorMode === 'DARK' ? darkImg : lightImg

  const solutionImageMap: Record<SolutionType, { normal: string; large: string }> = {
    elasticsearch: {
      normal: getThemeImage(elasticsearchSolutionImg, elasticsearchSolutionDarkImg),
      large: getThemeImage(elasticsearchSolutionLargeImg, elasticsearchSolutionLargeDarkImg),
    },
    observability: {
      normal: getThemeImage(observabilitySolutionImg, observabilitySolutionDarkImg),
      large: getThemeImage(observabilitySolutionLargeImg, observabilitySolutionLargeDarkImg),
    },
    security: {
      normal: getThemeImage(securitySolutionImg, securitySolutionDarkImg),
      large: getThemeImage(securitySolutionLargeImg, securitySolutionLargeDarkImg),
    },
  }

  const getSolutionImage = (
    selectedSolution: SolutionType | null,
    size: 'normal' | 'large' = 'large',
  ) => {
    if (selectedSolution) {
      return solutionImageMap[selectedSolution][size]
    }

    return size === 'large'
      ? getThemeImage(emptySolutionLargeImg, emptySolutionLargeDarkImg)
      : getThemeImage(emptySolutionImg, emptySolutionDarkImg)
  }

  return {
    getSolutionImage,
  }
}
